// SEQ-240 (audit B, rows 22 and 23): what period a month, quarter or year
// bucket stands for, read from the bucket key the RPC builds in
// `pnl_bucket_key`:
//   month / project_month   `<org>|2026-10`  or  `<org>|<project>|2026-10`
//   quarter                 `<org>|2026-Q3`
//   calendar_year           `<org>|2026`
//   financial_year          `<org>|FY26`   (1 July 2025 to 30 June 2026)
//
// The key CONTAINS the period, so two periods never share a key. That is why a
// comparison series cannot be matched by key, and why a quarter or year bar
// cannot be drilled by a field the row does not carry. Both fixes read the
// period here. Pure module, no React.

import type { DateRange } from "../../lib/pnl/fiscalYear";



export interface PeriodBucket {
  /** Everything before the period part of the key: the organisation, plus the project for project_month. */
  stem: string;
  /** Absolute month number (year x 12 + month 0 to 11) of the bucket's first month. */
  startMonth: number;
  /** How many calendar months the bucket spans: 1, 3 or 12. */
  months: number;
}

/** Reads a bucket's period from its key, or null when the key is not a period key of that grain. */
export function parsePeriodBucket(groupBy: string, groupKey: string): PeriodBucket | null {
  const cut = groupKey.lastIndexOf("|");
  if (cut < 0) return null;
  const stem = groupKey.slice(0, cut);
  const tail = groupKey.slice(cut + 1);

  if (groupBy === "month" || groupBy === "project_month") {
    const m = /^(\d{4})-(\d{2})$/.exec(tail);
    if (!m) return null;
    return { stem, startMonth: Number(m[1]) * 12 + Number(m[2]) - 1, months: 1 };
  }
  if (groupBy === "quarter") {
    const m = /^(\d{4})-Q([1-4])$/.exec(tail);
    if (!m) return null;
    return { stem, startMonth: Number(m[1]) * 12 + (Number(m[2]) - 1) * 3, months: 3 };
  }
  if (groupBy === "calendar_year") {
    const m = /^(\d{4})$/.exec(tail);
    if (!m) return null;
    return { stem, startMonth: Number(m[1]) * 12, months: 12 };
  }
  if (groupBy === "financial_year") {
    const m = /^FY(\d{2})$/.exec(tail);
    if (!m) return null;
    // FY26 runs 1 July 2025 to 30 June 2026.
    return { stem, startMonth: (2000 + Number(m[1]) - 1) * 12 + 6, months: 12 };
  }
  return null;
}

/** Months to step back to reach the prior period, by comparison kind. */
const SHIFT_MONTHS: Record<string, number> = {
  previous_month: 1,
  previous_quarter: 3,
  previous_year: 12,
  previous_financial_year: 12,
};

/**
 * The key part a comparison bucket must share with the current bucket it is
 * compared against, so the current bucket can look its prior up by offset.
 * Returns null when the key is not a period key, in which case the caller
 * falls back to matching on the whole key (project and company grains).
 *
 * The shift is rounded up to a whole number of buckets: a quarter chart
 * compared "previous month" steps back one quarter, not a third of one.
 */
export function comparisonMatchKey(
  groupBy: string,
  groupKey: string,
  kind: string | undefined,
  side: "current" | "prior",
): string | null {
  const bucket = parsePeriodBucket(groupBy, groupKey);
  if (!bucket || !kind) return null;
  const shift = SHIFT_MONTHS[kind];
  if (shift === undefined) return null;
  const buckets = Math.max(1, Math.round(shift / bucket.months));
  // Express both sides at the prior period's position.
  const position = side === "current" ? bucket.startMonth - buckets * bucket.months : bucket.startMonth;
  return `${bucket.stem}|${position}`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function monthStartIso(absMonth: number): string {
  return `${Math.floor(absMonth / 12)}-${pad((absMonth % 12) + 1)}-01`;
}

function monthEndIso(absMonth: number): string {
  const year = Math.floor(absMonth / 12);
  const month = (absMonth % 12) + 1;
  return `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`;
}

/** The exact calendar range a month, quarter or year bucket covers, or null when the key is not a period key. */
export function periodBucketRange(groupBy: string, groupKey: string): DateRange | null {
  const bucket = parsePeriodBucket(groupBy, groupKey);
  if (!bucket) return null;
  return { from: monthStartIso(bucket.startMonth), to: monthEndIso(bucket.startMonth + bucket.months - 1) };
}
